import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { axiosPrivate } from 'modules/networkTools/networkTools';
import { getTimezoneOffsetHours } from 'utils/dateHelpers/dateHelpers';

interface ClientNotesDTO {
  clientId: string;
  organizationId?: number;
  types?: string;
  page?: number;
  size?: number;
  firstRender?: boolean;
}

export const getClientNotes = createAsyncThunk(
  'clients/getClientNotes',
  async ({ clientId, page, size, types, firstRender }: ClientNotesDTO, { rejectWithValue }) => {
    const typesFiltered = types ? `&types=${types}` : '';
    try {
      const response = await axiosPrivate.get(
        `clients/${clientId}/profile/notes?page=${page}&size=${size}${typesFiltered}`
      );

      return { data: response.data, firstRender };
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getClientNote = createAsyncThunk(
  'clients/getClientNote',
  async (
    {
      clientId,
      noteId
    }: {
      clientId: string;
      noteId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosPrivate.get(`clients/${clientId}/profile/notes/${noteId}`);

      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);
// START DELETE AFTER PHP FLAG
export const postCreateNoteOld = createAsyncThunk(
  'clients/postCreateNote',
  async (
    {
      clientId,
      note,
      draft,
      noteType,
      clientReached,
      dateOfEncounter,
      nextContact,
      typeOfSupport,
      timeSpent
    }: {
      clientId: string;
      organizationId: string;
      note: string;
      draft: boolean;
      noteType: string;
      clientReached: string;
      dateOfEncounter: string;
      nextContact: string;
      typeOfSupport: string[];
      timeSpent: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosPrivate.put(`clients/${clientId}/profile/notes`, {
        noteType,
        clientReached,
        dateOfEncounter,
        nextContact,
        typeOfSupport,
        note,
        draft,
        timeSpent
      });
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const putUpdateNoteOld = createAsyncThunk(
  'clients/putUpdateNote',
  async (
    {
      id,
      clientId,
      note,
      draft,
      noteType,
      clientReached,
      dateOfEncounter,
      nextContact,
      typeOfSupport,
      timeSpent
    }: {
      id: string;
      clientId: string;
      note: string;
      draft: boolean;
      noteType: string;
      clientReached: string;
      dateOfEncounter: string;
      nextContact: string;
      typeOfSupport: string[];
      timeSpent: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosPrivate.put(
        `clients/${clientId}/profile/notes`,
        {
          id,
          noteType,
          clientReached,
          dateOfEncounter,
          nextContact,
          typeOfSupport,
          note,
          draft,
          timeSpent
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);
// END DELETE AFTER PHP FLAG
export const postCreateNote = createAsyncThunk(
  'clients/postCreateNote',
  async (
    {
      clientId,
      uuid,
      eventTimestamp,
      note,
      draft,
      noteType,
      clientReached,
      dateOfEncounter,
      startTimeOfEncounter,
      nextContact,
      typeOfSupport,
      timeSpent,
      timeSpentMin,
      siteId,
      zCodes,
      chwId,
      billingProviderId,
      managerId,
      documentationTime,
      otherProviderName,
      whoDidYouReach,
      whoWereYouTryingToReach,
      howDidYouTryThem,
      whatTypeOfPlaceWereYouIn,
      howDidYouReachThem
    }: {
      clientId: string;
      organizationId?: string;
      uuid: string;
      eventTimestamp: number;
      note: string;
      draft: boolean;
      noteType: string;
      clientReached: string;
      dateOfEncounter: string;
      startTimeOfEncounter: string;
      nextContact: string;
      typeOfSupport: string[];
      timeSpent?: string;
      timeSpentMin?: number;
      siteId: number;
      zCodes: { id: number; name: string }[];
      chwId?: string | number | null;
      billingProviderId?: string | null | number;
      managerId?: string | number | null;
      documentationTime?: number | null;
      otherProviderName?: string;
      whoDidYouReach?: string[];
      whoWereYouTryingToReach?: string;
      howDidYouTryThem?: string;
      whatTypeOfPlaceWereYouIn?: string;
      howDidYouReachThem?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      if (dayjs(startTimeOfEncounter, 'HH:mm:ss').isValid()) {
        startTimeOfEncounter = dayjs(startTimeOfEncounter, 'HH:mm:ss').format('hh:mm A');
      }
      const timezoneOffset = getTimezoneOffsetHours(dateOfEncounter, startTimeOfEncounter);
      const response = await axiosPrivate.put(
        `clients/${clientId}/profile/notes`,
        {
          uuid,
          eventTimestamp,
          noteType,
          clientReached,
          dateOfEncounter,
          startTimeOfEncounter,
          nextContact,
          typeOfSupport,
          note,
          draft,
          timeSpent,
          timeSpentMin,
          siteId,
          selectedZCodes: zCodes,
          chwId,
          billingProviderId,
          managerId,
          documentationTime,
          otherProviderName,
          whoDidYouReach,
          whoWereYouTryingToReach,
          howDidYouTryThem,
          whatTypeOfPlaceWereYouIn,
          howDidYouReachThem
        },
        {
          headers: {
            'x-time-zone-offset': timezoneOffset
          }
        }
      );
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const putUpdateNote = createAsyncThunk(
  'clients/putUpdateNote',
  async (
    {
      id,
      clientId,
      note,
      draft,
      noteType,
      clientReached,
      dateOfEncounter,
      startTimeOfEncounter,
      nextContact,
      typeOfSupport,
      timeSpentMin,
      timeSpent,
      siteId,
      zCodes,
      uuid,
      eventTimestamp,
      chwId,
      billingProviderId,
      managerId,
      documentationTime,
      otherProviderName,
      whoDidYouReach,
      whoWereYouTryingToReach,
      howDidYouTryThem,
      whatTypeOfPlaceWereYouIn,
      howDidYouReachThem
    }: {
      id?: string;
      clientId: string;
      note: string | null;
      draft: boolean;
      noteType: string;
      clientReached: string;
      dateOfEncounter: string;
      startTimeOfEncounter: string;
      nextContact: string;
      typeOfSupport: string[];
      timeSpentMin?: number;
      timeSpent?: number;
      siteId: number | null;
      zCodes: { id: number; name: string }[];
      uuid: string;
      eventTimestamp: number;
      chwId?: string | number | null;
      billingProviderId?: string | null | number;
      managerId?: string | null | number;
      documentationTime?: number | null;
      otherProviderName?: string;
      whoDidYouReach?: string[];
      whoWereYouTryingToReach?: string;
      howDidYouTryThem?: string;
      whatTypeOfPlaceWereYouIn?: string;
      howDidYouReachThem?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      if (dayjs(startTimeOfEncounter, 'HH:mm:ss').isValid()) {
        startTimeOfEncounter = dayjs(startTimeOfEncounter, 'HH:mm:ss').format('hh:mm A');
      }
      const timezoneOffset = getTimezoneOffsetHours(dateOfEncounter, startTimeOfEncounter);
      const response = await axiosPrivate.put(
        `clients/${clientId}/profile/notes`,
        {
          id,
          uuid,
          eventTimestamp,
          noteType,
          clientReached,
          dateOfEncounter,
          startTimeOfEncounter,
          nextContact,
          typeOfSupport,
          note,
          draft,
          timeSpentMin,
          timeSpent,
          siteId,
          selectedZCodes: zCodes,
          chwId,
          billingProviderId,
          managerId,
          documentationTime,
          otherProviderName,
          whoDidYouReach,
          whoWereYouTryingToReach,
          howDidYouTryThem,
          whatTypeOfPlaceWereYouIn,
          howDidYouReachThem
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-time-zone-offset': timezoneOffset
          }
        }
      );
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const deleteNoteInput = createAsyncThunk(
  'clients/deleteNoteInput',
  async ({ clientId, noteId }: { clientId: string; noteId: string }, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.delete(`clients/${clientId}/profile/notes/${noteId}`, {
        data: {
          clientId,
          noteId
        },
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getAddendums = createAsyncThunk(
  'clients/getAddendums',
  async (
    {
      noteId
    }: {
      noteId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosPrivate.get(`client/note/${noteId}/addendums`);

      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const postCreateAddendum = createAsyncThunk(
  'clients/postCreateAddendum',
  async (
    {
      noteId,
      content
    }: {
      noteId: string;
      content: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosPrivate.post(`client/note/${noteId}/addendum`, { content });

      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const putEditAddendum = createAsyncThunk(
  'clients/putEditAddendum',
  async (
    {
      addendumId,
      noteId,
      content
    }: {
      noteId: string;
      addendumId: number;
      content: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosPrivate.put(`client/note/${noteId}/addendum/${addendumId}`, {
        content
      });

      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const patchEditAddendum = createAsyncThunk(
  'clients/patchEditAddendum',
  async (
    {
      addendumId,
      noteId,
      content,
      status
    }: {
      status: 'FINAL' | 'DRAFT';
      noteId: string;
      addendumId: number;
      content: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosPrivate.patch(`client/note/${noteId}/addendum/${addendumId}`, {
        content,
        status
      });

      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const deleteAddendum = createAsyncThunk(
  'clients/deleteAddendum',
  async (
    {
      addendumId,
      noteId
    }: {
      noteId: string;
      addendumId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosPrivate.delete(`client/note/${noteId}/addendum/${addendumId}`);

      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getUsersImageNotes = createAsyncThunk(
  'clients/getUsersImageNotes',
  async (
    {
      profileImageKey,
      noteId,
      notePopup,
      notesList,
      noteAddendums
    }: {
      profileImageKey: string;
      userId?: number | string;
      noteId?: string;
      notePopup?: boolean;
      notesList?: boolean;
      noteAddendums?: boolean;
    },
    { rejectWithValue }
  ) => {
    const url = `setup/organization/user/profile-image?key=${profileImageKey}`;
    try {
      const response = await axiosPrivate.get(url, { responseType: 'blob' });
      return {
        data: response.data,
        noteId,
        notePopup,
        notesList,
        noteAddendums
      };
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getSites = createAsyncThunk('clients/getSites', async (_, { rejectWithValue }) => {
  try {
    const response = await axiosPrivate.get('clients/programs/sites');

    return response.data;
  } catch (error: any | AxiosError) {
    if (error.response && error.response.data.errors) {
      return rejectWithValue(error.response.data.errors);
    } else {
      return rejectWithValue(error.message);
    }
  }
});

export const getFavoriteZCodes = createAsyncThunk(
  'clients/getFavoriteZCodes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(`z-codes/favourites`);
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getZCodes = createAsyncThunk(
  'clients/getZCodes',
  async ({ query }: { query: string }, { rejectWithValue, signal }) => {
    try {
      const response = await axiosPrivate.get(`z-codes/search?query=${query}`, {
        signal
      });
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getBillingConfig = createAsyncThunk(
  'clients/getBillingConfig',
  async ({ organizationId }: { organizationId: string }, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(`clients/${organizationId}/billing-config`);
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getBillingPrograms = createAsyncThunk(
  'clients/getBillingPrograms',
  async ({ organizationId }: { organizationId: string }, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(`clients/${organizationId}/billing-programs`);
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getBillingProviders = createAsyncThunk(
  'clients/getBillingProviders',
  async ({ organizationId }: { organizationId: string }, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(`clients/${organizationId}/billing-providers`);
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);
